import {ShadowContainer} from '@App/ShadowContainer';
import {ProximityHandler, ProximityHandlerResult} from "@App/ProximityHandler";
import {SnapHandler} from "@App/SnapHandler";
import {DraggableConfiguration} from "@App/DragDropHandler";
import {DotNetObjectReference} from "@App/BlazorTypes";
import {DragImageCreator} from "@App/DragImageCreator";
export class DragDropManager {
    private proximityHandler: ProximityHandler;
    private shadowContainer: ShadowContainer;
    private snapHandler: SnapHandler;
    private dragImageCreator: DragImageCreator;
    
    private draggingElement: boolean = false;
    private elementsData: WeakMap<HTMLElement, DraggableConfiguration> = new WeakMap();

    private proximityScheduled = false;
    
    constructor() {
        this.shadowContainer = new ShadowContainer("dragShadow");
        this.proximityHandler = new ProximityHandler()
        this.snapHandler = new SnapHandler();
        this.dragImageCreator = new DragImageCreator();
    }
    
    registerElement(element: HTMLElement, configuration: DraggableConfiguration): void {
        this.registerDraggable(element, configuration);
        this.registerDraggableShadow(element, configuration);
    }
    
    disposeElement(element: HTMLElement): void {
        element.removeEventListener("dragstart", (e) => this.handleDragStart(e, element));
        element.removeEventListener("drag", (e) => this.handleDrag(e, element));
        if(this.elementsData.has(element)) {
            element.removeEventListener("dragend", (e: DragEvent) => this.handleDragEnd(e, element, 
                this.elementsData.get(element)!.proximityCallback));
        }
        this.elementsData.delete(element);
    }

    private registerDraggable(el: HTMLElement, configuration: DraggableConfiguration): void {
        this.elementsData.set(el, configuration);
    }
    private registerDraggableShadow(element: HTMLElement, configuration: DraggableConfiguration): void {
        element.addEventListener("dragstart", (e) => this.handleDragStart(e, element));
        element.addEventListener("drag", (e) => this.handleDrag(e, element));
        element.addEventListener("dragend", (e: DragEvent) => this.handleDragEnd(e, element, configuration.proximityCallback));
    }
    

    private async handleDrag(e: DragEvent, element: HTMLElement): Promise<void> {
        if (!this.draggingElement) return;

        if (!this.shadowContainer.isVisible()) {
            this.shadowContainer.show();
        }

        this.shadowContainer.smoothMoveTo(e.clientX, e.clientY);

        if (!this.proximityScheduled) {
            this.proximityScheduled = true;
            requestAnimationFrame(() => {
                this.runProximityCheck(e, element);
                this.proximityScheduled = false;
            });
        }
        
        return;
    }

    private runProximityCheck(e: DragEvent, element: HTMLElement) {
        const elementConfig = this.elementsData.get(element);
        if (!elementConfig) return;

        if (elementConfig.proximityCheckDistance > 0) {
            const nearest = this.proximityHandler.GetClosestToPoint(
                e.clientX,
                e.clientY,
                ".drag-accept",
                elementConfig.proximityCheckDistance
            );
            this.proximityHandler.SetProximityAttributes(nearest);

            if (elementConfig.snapDistance > 0) {
                this.handleSnapPreview(nearest, elementConfig.snapDistance);
            }
        }
    }

    private handleSnapPreview(result: ProximityHandlerResult, snapLimit: number) {
        if(!result.DidChange) return;
        if(!result.CurrentlyClosest) {
            this.snapHandler.CleanUpSnappedElements();
            return;
        }
        
        if(result.CurrentlyClosest.classList.contains("drag-snap")) {
            if (result.ProximityToClosest <= snapLimit) {
                
                const shadowContents: HTMLElement | null = this.shadowContainer.getContents();
                if(!shadowContents) return;

                this.snapHandler.SnapToElement(result.CurrentlyClosest, shadowContents);
            }
        }
    }
        
    private handleMouseMove = (e: MouseEvent): void => {
        this.shadowContainer.smoothMoveTo(e.clientX, e.clientY);
    };

    private handleDragStart(e: DragEvent, element: HTMLElement): boolean {
        
        if(element.getAttribute("draggable") !== "true") {
            e.preventDefault();
            return false;
        }
                
        if(this.draggingElement) return false;
        if (!element || !e?.dataTransfer) return false;

        const dragImage = this.dragImageCreator.createFromElement(element);

        if (!this.draggingElement) {
            this.draggingElement = true;
            this.shadowContainer.render(dragImage);
            this.shadowContainer.show();
            document.addEventListener("mousemove", this.handleMouseMove);
        }

        e.dataTransfer.setDragImage(document.createElement("div"), 0, 0);// dragImage, rect.width / 2, rect.height / 2);

        setTimeout(() => {
            element.style.setProperty("opacity", "0.4");
        }, 10);
        
        return true;
    }

    private handleDragEnd(e: DragEvent, element: HTMLElement, proximityCallback: (result:ProximityHandlerResult) => void): void {
        this.draggingElement = false;
        const elementConfig = this.elementsData.get(element);
        
        if (elementConfig && elementConfig.snapDistance > 0) {
            let nearest = this.proximityHandler.GetClosestToPoint(e.clientX, e.clientY, ".drag-accept", elementConfig?.snapDistance ?? 50);
            proximityCallback(nearest);
        }
        
        element.style.setProperty("opacity", "1");
        this.shadowContainer.hide();
        this.proximityHandler.CleanUpProximityAttributes();
        this.snapHandler.CleanUpSnappedElements();
        document.removeEventListener("mousemove", this.handleMouseMove);
    }
}